import React from 'react';
import Icon from 'components/AppIcon';

export const SkeletonPulse = ({ className = '' }) => (
  <div className={`animate-pulse rounded bg-white/5 ${className}`} />
);

export const SkeletonCard = ({ className = '' }) => (
  <div className={`rounded-xl border border-border bg-card p-4 animate-pulse ${className}`}>
    <div className="flex items-start gap-3 mb-3">
      <div className="w-12 h-12 rounded-xl bg-white/5 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-3/4 rounded bg-white/5" />
        <div className="h-3 w-1/2 rounded bg-white/5" />
      </div>
    </div>
    <div className="space-y-2">
      <div className="h-3 w-full rounded bg-white/5" />
      <div className="h-3 w-5/6 rounded bg-white/5" />
    </div>
    <div className="mt-4 h-8 w-full rounded-lg bg-white/5" />
  </div>
);

export const StatCardSkeleton = ({ className = '' }) => (
  <div className={`rounded-xl border border-border bg-card p-4 animate-pulse ${className}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="h-3 w-20 rounded bg-white/5" />
      <div className="w-8 h-8 rounded-lg bg-white/5" />
    </div>
    <div className="h-7 w-28 rounded bg-white/5 mb-2" />
    <div className="h-3 w-16 rounded bg-white/5" />
  </div>
);

export const TrendingStockSkeleton = ({ className = '' }) => (
  <div className={`flex items-center justify-between p-3 rounded-lg border border-border/50 animate-pulse ${className}`}>
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-white/5" />
      <div className="space-y-1.5">
        <div className="h-3.5 w-16 rounded bg-white/5" />
        <div className="h-2.5 w-24 rounded bg-white/5" />
      </div>
    </div>
    <div className="text-right space-y-1.5">
      <div className="h-3.5 w-16 rounded bg-white/5 ml-auto" />
      <div className="h-2.5 w-12 rounded bg-white/5 ml-auto" />
    </div>
  </div>
);

export const SkeletonRow = ({ cols = 4, className = '' }) => (
  <tr className={`border-b border-border/50 ${className}`}>
    {Array(cols)?.fill(0)?.map((_, i) => (
      <td key={i} className="p-4">
        <div className="h-4 rounded bg-white/5 animate-pulse" style={{ width: i === 0 ? '2rem' : i === 1 ? '8rem' : '5rem', marginLeft: i > 1 ? 'auto' : undefined }} />
      </td>
    ))}
  </tr>
);

export const ShimmerRow = ({ cols = 6, className = '' }) => (
  <tr className={`border-b border-border/50 ${className}`}>
    {Array(cols)?.fill(0)?.map((_, i) => (
      <td key={i} className="p-3">
        <div
          className="h-4 rounded relative overflow-hidden"
          style={{
            width: i === 0 ? '3rem' : i === 1 ? '9rem' : i === 2 ? '6rem' : '5rem',
            background: 'rgba(255,255,255,0.04)',
            marginLeft: i > 2 ? 'auto' : undefined,
          }}
        >
          <div
            className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite]"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
            }}
          />
        </div>
      </td>
    ))}
  </tr>
);

export const SkeletonChart = ({ height = 200, className = '' }) => (
  <div className={`rounded-xl border border-border bg-card p-4 animate-pulse ${className}`}>
    <div className="h-4 w-32 rounded bg-white/5 mb-4" />
    <div className="rounded-lg bg-white/5" style={{ height }} />
  </div>
);

export const ChartSkeleton = ({ height = 200, className = '' }) => (
  <div className={`rounded-xl border border-border bg-card p-4 animate-pulse ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <div className="h-4 w-32 rounded bg-white/5" />
      <div className="h-6 w-20 rounded bg-white/5" />
    </div>
    <div className="rounded-lg bg-white/5 relative overflow-hidden" style={{ height }}>
      <div
        className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)' }}
      />
    </div>
  </div>
);

export const SkeletonText = ({ lines = 3, className = '' }) => (
  <div className={`space-y-2 animate-pulse ${className}`}>
    {Array(lines)?.fill(0)?.map((_, i) => (
      <div key={i} className="h-3 rounded bg-white/5" style={{ width: i === lines - 1 ? '60%' : '100%' }} />
    ))}
  </div>
);

export const SkeletonAvatar = ({ size = 40, className = '' }) => (
  <div
    className={`rounded-full bg-white/5 animate-pulse flex-shrink-0 ${className}`}
    style={{ width: size, height: size }}
  />
);

export const EmptyState = ({
  icon,
  title,
  description,
  action,
  actionLabel,
  className = '',
}) => (
  <div className={`flex flex-col items-center justify-center py-16 px-6 text-center ${className}`}>
    {icon && (
      <div className="w-16 h-16 rounded-2xl bg-primary/5 border border-primary/10 flex items-center justify-center mb-4">
        {typeof icon === 'string' ? (
          <Icon name={icon} size={28} color="var(--color-primary)" />
        ) : (
          icon
        )}
      </div>
    )}
    <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
    {description && (
      <p className="text-sm text-muted-foreground max-w-xs mb-6 leading-relaxed">{description}</p>
    )}
    {action && actionLabel && (
      <button
        onClick={action}
        className="px-5 py-2.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-sm font-semibold hover:bg-primary hover:text-zinc-950 transition-all"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export default SkeletonCard;
